// src/pages/Home.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { fetchModelCatalog } from '../constants/modelCatalog';
import ThemeToggle from './components/ThemeToggle';

const CURL_CMD = `curl -N -X POST https://qualitycompute.henosis.us/v1/responses/stream \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer QC_YOUR_API_KEY" \\
  -H "Idempotency-Key: demo-poem-001" \\
  -d '{
    "model": "o4-mini-B10",
    "input": "Generate a poem about space.",
    "stream": true
  }'`;

const PROVIDER_STYLES = {
  openai: 'bg-emerald-500',
  anthropic: 'bg-orange-500',
  gemini: 'bg-blue-500',
  xai: 'bg-slate-800 dark:bg-slate-600',
  deepseek: 'bg-indigo-500',
  kimi: 'bg-purple-500',
  glm: 'bg-teal-500',
};

const STEPS = [
  {
    num: '01',
    title: 'Drafts run in parallel',
    desc: 'Append -B10 to any model id and ten drafts are generated concurrently — no orchestration code on your side.',
  },
  {
    num: '02',
    title: 'A judge picks the winner',
    desc: 'A judge model scores every draft against your prompt and selects the strongest one (or synthesizes a merged answer).',
  },
  {
    num: '03',
    title: 'Only the best streams back',
    desc: 'The winning answer arrives as a normal SSE stream, with full token usage and cost accounting in the final event.',
  },
];

const STRATEGIES = [
  {
    tag: '-B[N]',
    name: 'Best-of-N',
    desc: 'N parallel drafts, one winner. The fastest way to trade compute for quality — a suffix on the model id is all it takes.',
  },
  {
    tag: 'judge_selection',
    name: 'Judge selection',
    desc: 'Bring your own judge: a separate model you choose scores the drafts and picks the best response.',
  },
  {
    tag: 'synthesis',
    name: 'Synthesis',
    desc: 'Instead of picking one draft, a synthesis model merges the strongest ideas from all of them into a single answer.',
  },
  {
    tag: 'collaborative',
    name: 'Collaborative',
    desc: 'A team-leader model refines the work of a student cohort over multiple turns — multi-model deliberation on demand.',
  },
];

export default function Home() {
  const [copied, setCopied] = useState(false);
  const [elevated, setElevated] = useState(false);
  const [modelCatalog, setModelCatalog] = useState([]);

  useEffect(() => {
    const onScroll = () => setElevated(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadModels() {
      const models = await fetchModelCatalog();
      if (!cancelled) {
        setModelCatalog(models);
      }
    }

    loadModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const copyCurl = () => {
    navigator.clipboard.writeText(CURL_CMD);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const providers = useMemo(() => {
    const grouped = new Map();
    for (const model of modelCatalog) {
      const provider = model.provider || 'unknown';
      if (!grouped.has(provider)) {
        grouped.set(provider, { name: provider, count: 0 });
      }
      grouped.get(provider).count += 1;
    }
    return Array.from(grouped.values());
  }, [modelCatalog]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 antialiased transition-colors">
      {/* Ambient background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[52rem] h-[36rem] rounded-full bg-gradient-to-br from-indigo-200/50 via-purple-200/40 to-transparent dark:from-indigo-800/20 dark:via-purple-800/15 blur-3xl" />
        <div className="absolute top-[42rem] -left-40 w-[30rem] h-[30rem] rounded-full bg-purple-100/40 dark:bg-purple-900/15 blur-3xl" />
      </div>

      {/* Pill header */}
      <header className="sticky top-4 z-50 mx-4 sm:mx-6">
        <div
          className={`max-w-6xl mx-auto flex items-center justify-between pl-3 pr-3 sm:pl-4 sm:pr-3 py-2.5 rounded-full bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-slate-200/80 dark:border-slate-700/80 transition-shadow ${
            elevated ? 'shadow-lg shadow-slate-900/5 dark:shadow-black/30' : 'shadow-sm'
          }`}
        >
          <Link to="/" className="flex items-center gap-2.5 font-semibold tracking-tight">
            <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white text-sm font-bold shadow-md shadow-indigo-500/25">
              QC
            </span>
            <span className="hidden sm:inline-block text-[15px]">Quality Compute</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600 dark:text-slate-300">
            <a href="#how-it-works" className="hover:text-slate-900 dark:hover:text-white transition-colors">How it works</a>
            <a href="#strategies" className="hover:text-slate-900 dark:hover:text-white transition-colors">Strategies</a>
            <a href="#pricing" className="hover:text-slate-900 dark:hover:text-white transition-colors">Pricing</a>
            <Link to="/api-reference" className="hover:text-slate-900 dark:hover:text-white transition-colors">Docs</Link>
          </nav>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <Link
              to="/login"
              className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800 transition-colors"
            >
              Login
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-medium hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors shadow-sm"
            >
              Get API Key
            </Link>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="relative flex-1 w-full">
        {/* Hero */}
        <section className="px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-24">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Copy */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-600 dark:text-slate-300 shadow-sm">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                Streaming-first · Best-of-N built in
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.08]">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 dark:from-indigo-400 dark:via-purple-400 dark:to-indigo-400 text-transparent bg-clip-text">
                  Parallel compute
                </span>{' '}
                for frontier models
              </h1>
              <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed max-w-xl">
                One streaming API across every major provider. Turn any model into best-of-N with a
                suffix — drafts run in parallel, a judge picks the winner, and only the best answer
                streams back.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold hover:from-indigo-500 hover:to-purple-500 transition-colors shadow-lg shadow-indigo-500/25"
                >
                  Get your API key
                </Link>
                <Link
                  to="/api-reference"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shadow-sm"
                >
                  Read the docs
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
              {/* Stat strip */}
              <div className="flex items-center gap-8 pt-2">
                <div>
                  <div className="text-2xl font-bold tracking-tight">{providers.length || '7'}</div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-0.5">Providers</div>
                </div>
                <div className="w-px h-10 bg-slate-200 dark:bg-slate-800" />
                <div>
                  <div className="text-2xl font-bold tracking-tight">{modelCatalog.length || '—'}</div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-0.5">Live models</div>
                </div>
                <div className="w-px h-10 bg-slate-200 dark:bg-slate-800" />
                <div>
                  <div className="text-2xl font-bold tracking-tight">5</div>
                  <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-0.5">Strategies</div>
                </div>
              </div>
            </div>

            {/* Terminal */}
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-indigo-500/15 via-purple-500/10 to-transparent rounded-3xl blur-xl" aria-hidden="true" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/70 bg-slate-900 shadow-2xl shadow-slate-900/25 dark:shadow-black/50">
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 bg-slate-800/70 border-b border-slate-700/70">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-red-400/90" />
                      <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                      <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    </div>
                    <span className="text-xs font-medium text-slate-400 font-mono">best-of-10 in one request</span>
                  </div>
                  <button
                    onClick={copyCurl}
                    className="text-xs font-medium text-slate-300 bg-slate-700/60 border border-slate-600/60 px-3 py-1 rounded-md hover:bg-slate-600/60 transition-colors"
                  >
                    {copied ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
                <pre className="p-4 sm:p-6 overflow-x-auto text-[13px] leading-relaxed font-mono">
                  <code className="block whitespace-pre text-slate-300">
                    <span className="text-purple-400">curl</span> -N -X POST <span className="text-sky-400">https://qualitycompute.henosis.us/v1/responses/stream</span> \{'\n'}
                    {'  '}-H <span className="text-emerald-400">"Content-Type: application/json"</span> \{'\n'}
                    {'  '}-H <span className="text-emerald-400">"Authorization: Bearer QC_YOUR_API_KEY"</span> \{'\n'}
                    {'  '}-H <span className="text-emerald-400">"Idempotency-Key: demo-poem-001"</span> \{'\n'}
                    {'  '}-d <span className="text-amber-300">'</span>{'{'}{'\n'}
                    {'    '}<span className="text-indigo-300">"model"</span>: <span className="text-emerald-400">"o4-mini-B10"</span>,{'\n'}
                    {'    '}<span className="text-indigo-300">"input"</span>: <span className="text-emerald-400">"Generate a poem about space."</span>,{'\n'}
                    {'    '}<span className="text-indigo-300">"stream"</span>: <span className="text-sky-400">true</span>{'\n'}
                    {'  '}{'}'}<span className="text-amber-300">'</span>
                  </code>
                </pre>
                <div className="px-4 sm:px-6 pb-4 flex items-center gap-2 text-xs font-mono text-slate-500">
                  <span className="text-emerald-400">→</span> the <span className="text-slate-300">-B10</span> suffix fans out 10 drafts and streams back the best one
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-24">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-3">How it works</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                More compute, better answers
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                Traditional APIs give you a single shot. Quality Compute spends extra inference in
                parallel and returns only what survives the judge.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mb-12">
              {STEPS.map((step) => (
                <div
                  key={step.num}
                  className="group relative p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-lg dark:hover:border-slate-700 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="text-sm font-mono font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 text-transparent bg-clip-text mb-4">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>

            {/* Showcase panel */}
            <div className="relative overflow-hidden rounded-3xl bg-slate-900 shadow-2xl shadow-slate-900/20 dark:shadow-black/40 dark:border dark:border-slate-800">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-slate-900 to-purple-900/50" aria-hidden="true" />
              <div className="relative p-6 sm:p-10 md:p-14 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                <div className="text-white space-y-6">
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">See the difference</h3>
                  <p className="text-slate-300 leading-relaxed">
                    Every draft is a chance at a better answer. Quality Compute generates the
                    candidates, judges them against your prompt, and bills you transparently for
                    exactly the compute used.
                  </p>
                  <ul className="space-y-3">
                    {[
                      'Parallel drafts with a single API call',
                      'Judge, synthesis, and collaborative modes',
                      'Full usage and cost detail on every response',
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-3 text-slate-200">
                        <span className="inline-grid place-items-center w-5 h-5 rounded-full bg-indigo-500/90 flex-shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-sm sm:text-base">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 mt-2 px-6 py-3 bg-white text-slate-900 font-semibold rounded-xl hover:bg-indigo-50 transition-colors"
                  >
                    Get started today
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </div>

                {/* Candidate visualization */}
                <div className="relative bg-white/[0.06] backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono text-slate-400">strategy: best_of_n</span>
                    <span className="text-xs text-indigo-200 px-2.5 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/20">
                      Quality Compute
                    </span>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3.5 rounded-xl bg-white/[0.04] border border-white/10">
                      <p className="text-slate-400 text-xs font-mono mb-1">candidate 1</p>
                      <p className="text-slate-400 text-xs">Good answer but lacks detail…</p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-indigo-500/25 border border-indigo-300/30 shadow-lg shadow-indigo-950/40">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-indigo-100 text-xs font-mono">candidate 2</p>
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-indigo-500 text-white px-2 py-0.5 rounded-full">
                          Selected
                        </span>
                      </div>
                      <p className="text-white/90 text-xs">
                        Exceptional response with detailed analysis and clear explanations…
                      </p>
                    </div>
                    <div className="p-3.5 rounded-xl bg-white/[0.04] border border-white/10">
                      <p className="text-slate-400 text-xs font-mono mb-1">candidate 3</p>
                      <p className="text-slate-400 text-xs">Accurate but too verbose…</p>
                    </div>
                  </div>
                  <div className="mt-4 text-right text-xs text-indigo-200/80 font-mono">
                    judge → best candidate streamed back
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Strategies */}
        <section id="strategies" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-24">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-3">Strategies</div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
                Four ways to spend compute wisely
              </h2>
              <p className="text-lg text-slate-600 dark:text-slate-400">
                Opt in per request — with explicit strategy fields or the{' '}
                <code className="text-[15px] px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-indigo-700 dark:text-indigo-300">-B[N]</code>{' '}
                model-suffix shorthand.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              {STRATEGIES.map((s) => (
                <div
                  key={s.name}
                  className="group p-6 sm:p-7 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-800 transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">{s.name}</h3>
                    <code className="text-xs px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-900 text-indigo-700 dark:text-indigo-300 font-medium">
                      {s.tag}
                    </code>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing & Models */}
        <section id="pricing" className="px-4 sm:px-6 py-16 sm:py-24 scroll-mt-24">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pricing card */}
            <div className="relative p-7 sm:p-9 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 -mt-16 -mr-16 bg-gradient-to-bl from-indigo-100/80 dark:from-indigo-900/30 to-transparent rounded-full" aria-hidden="true" />
              <div className="relative">
                <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-4">Pricing</div>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="text-5xl sm:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 text-transparent bg-clip-text">
                    0%
                  </span>
                  <span className="text-xl text-slate-600 dark:text-slate-300 font-medium">markup on model usage</span>
                </div>
                <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                  Choose a platform subscription for access, then add credits as you use the API.
                  Credits are sold at par and model usage is billed at provider cost — no hidden spread
                  inside token pricing. Create an account, verify your email, choose a plan, and mint
                  an API key from the dashboard.
                </p>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-700/80 mb-8">
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    Example cost breakdown
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-300">Provider model spend</span>
                      <span className="font-medium font-mono">$6.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-300">Quality Compute markup</span>
                      <span className="font-medium font-mono text-emerald-600 dark:text-emerald-400">$0.00</span>
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between font-semibold">
                      <span>You pay</span>
                      <span className="font-mono">$6.00</span>
                    </div>
                  </div>
                </div>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center w-full px-6 py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-500 hover:to-purple-500 transition-colors shadow-lg shadow-indigo-500/25"
                >
                  Get Your API Key
                </Link>
              </div>
            </div>

            {/* Models card */}
            <div className="relative p-7 sm:p-9 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 -mt-16 -mr-16 bg-gradient-to-bl from-purple-100/80 dark:from-purple-900/30 to-transparent rounded-full" aria-hidden="true" />
              <div className="relative">
                <div className="text-sm font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-4">Models</div>
                <h3 className="text-2xl font-bold tracking-tight mb-3">Every major provider, one API</h3>
                <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
                  The list below is rendered live from the backend model catalog, so it always
                  reflects what you can actually call.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {providers.map((provider) => (
                    <div
                      key={provider.name}
                      className="flex items-center gap-3 p-3 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-slate-800 hover:border-indigo-200 dark:hover:border-indigo-800 hover:shadow-sm transition-all"
                    >
                      <span
                        className={`inline-grid place-items-center w-9 h-9 rounded-lg text-white text-sm font-bold flex-shrink-0 ${
                          PROVIDER_STYLES[provider.name] || 'bg-slate-400'
                        }`}
                      >
                        {provider.name.charAt(0).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <span className="font-semibold text-sm block capitalize truncate">{provider.name}</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {provider.count} model{provider.count === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/api-reference"
                  className="inline-flex items-center justify-center w-full px-6 py-3.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 font-semibold rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 hover:border-slate-400 dark:hover:border-slate-500 transition-colors"
                >
                  View All Supported Models
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="px-4 sm:px-6 pb-20 sm:pb-28">
          <div className="max-w-6xl mx-auto relative overflow-hidden rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 shadow-2xl shadow-indigo-500/25 dark:shadow-indigo-950/50">
            <div className="relative px-6 sm:px-12 py-12 sm:py-16 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
                Ship better answers today
              </h2>
              <p className="text-indigo-100 text-lg max-w-xl mx-auto mb-8">
                Register, mint a key, and add <span className="font-mono text-white">-B10</span> to
                your model id. That's the whole integration.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl bg-white text-indigo-700 font-semibold hover:bg-indigo-50 transition-colors shadow-lg"
                >
                  Get your API key
                </Link>
                <Link
                  to="/api-reference"
                  className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl border border-white/40 text-white font-semibold hover:bg-white/10 transition-colors"
                >
                  Read the docs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative py-10 border-t border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center w-7 h-7 rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700 text-white text-[10px] font-bold">
              QC
            </span>
            <span>© {new Date().getFullYear()} Quality Compute</span>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/privacy" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
